import { useState, useEffect } from "react";
import { OrderSuccessHeader } from "./OrderSuccessHeader";
import { OrderSuccessAnimation } from "./OrderSuccessAnimation";
import { OrderDetails } from "./OrderDetails";
import { OrderActions } from "./OrderActions";
import { useNavigate, useSearchParams } from "react-router";
import { OrderData } from "@nusrif/types";
import { getOrderDetails, trackGAEvent, trackMetaPixel } from "@nusrif/services";
import { useCart } from "@nusrif/contexts";
export const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const { refreshCart } = useCart();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [showAnimation, setShowAnimation] = useState(true);
  const orderId = searchParams.get("orderId");
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!orderId) {
        navigate("/");
        return;
      }
      // Fetch order data from API
      const orderData = await getOrderDetails(orderId);
      const order = {
        ...orderData.data.storeOrder,
        orderId: orderData.data.orderId
      };
      setOrderData(order);
      
      // Track successful purchase with Google Analytics
      trackGAEvent('purchase', {
        transaction_id: order.orderId,
        value: order.finalTotal,
        currency: 'INR', // Assuming INR currency
        items: order.items.map(item => ({
          item_id: item.productId,
          item_name: item.name,
          item_category: item.subtitle || '',
          price: item.price,
          quantity: item.quantity,
        })),
      });

      // Track successful purchase with Meta Pixel
      trackMetaPixel('Purchase', {
        content_name: 'Order',
        content_ids: order.items.map(item => item.productId),
        content_type: 'product',
        value: order.finalTotal,
        currency: 'INR',
        num_items: order.items.length,
      });
      
      // Refresh user cart
      refreshCart();
      // Hide animation after 3 seconds
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 3000);
      // Cleanup timer when component unmounts
      return () => clearTimeout(timer);
    };
    fetchOrderData();
    // Cleanup function for useEffect
    return () => {
      setShowAnimation(false);
    };
  }, [searchParams, navigate]);
  const handleContinueShopping = () => {
    navigate("/");
  };
  const handleTrackOrder = () => {
    console.log("Track order:", orderId);
    // Navigate to order tracking page
  };
  if (!orderData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e4aa8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Fixed Header */}
      <OrderSuccessHeader />
      {/* Main Content */}
      <div className="pt-20 pb-32">
        {showAnimation ? (
          <OrderSuccessAnimation orderId={orderId!} />
        ) : (
          <div className="px-5 space-y-6">
            {/* Success Message */}
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-black mb-2">
                Order Placed Successfully!
              </h1>
              <p className="text-gray-600">Thank you for your purchase</p>
            </div>
            {/* Order ID */}
            <div className="p-4 bg-white rounded-lg border border-gray-100 shadow-sm text-center">
              <p className="text-sm text-gray-600 mb-1">Order ID</p>
              <p className="text-xl font-bold text-[#1e4aa8]">
                {orderData.orderId}
              </p>
            </div>
            {/* Order Details */}
            <OrderDetails orderData={orderData} />
          </div>
        )}
      </div>
      {/* Fixed Action Buttons */}
      {!showAnimation && (
        <OrderActions
          onContinueShopping={handleContinueShopping}
          onTrackOrder={handleTrackOrder}
        />
      )}
    </div>
  );
};
